import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import VacationStatusDot from '@/pages/Freelancer/components/VacationStatusDot';
import VacationTypeBadge from '@/pages/Freelancer/components/VacationTypeBadge';
import FreelancerVacationRequestModal from '@/pages/Freelancer/components/vacation/FreelancerVacationRequestModal';
import { FreelancerHolidayRequestResource } from '@/types/resources';
import { api } from '@/utils/api';
import { VacationStatus } from '@/utils/enums';

import VacationApprovalModal from './VacationApprovalModal';

interface Props {
  request: FreelancerHolidayRequestResource;
  activeStatus: {
    value: VacationStatus;
    title: string;
  };
}

const Tr = styled.tr<{ $isClickable: boolean }>`
  ${({ $isClickable, theme }) =>
    $isClickable &&
    css`
      Td {
        transition: background-color ease-out 0.2s;
      }
      cursor: pointer;
      &:hover {
        Td {
          background-color: ${theme.colors.grayscaleLight};
        }
      }
    `}
`;

const VacationPeriodTd = styled(Td)`
  white-space: nowrap;
`;

const VacationApprovalTableRow: FC<Props> = ({ request, activeStatus }) => {
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const canManageRequests = useHasPermission(
    'manage freelancers',
    'view companies vacations',
    'view departments vacations',
  );

  const availableDays = request.holidayBalance?.availableDays;
  const availableDaysText = availableDays
    ? `${availableDays} ${availableDays === 1 ? 'day' : 'days'}`
    : '0 days';
  const deductedDaysText = `${request.totalDays} ${request.totalDays === 1 ? 'day' : 'days'}`;
  const vacationPeriod = `${request.startDate ? DateTime.fromISO(request.startDate).toFormat('yyyy/LL/dd') : '-'} - ${request.endDate ? DateTime.fromISO(request.endDate).toFormat('yyyy/LL/dd') : '-'}`;

  const onRowClick = () => {
    if (activeStatus.value !== VacationStatus.Pending) {
      return;
    }
    setShowApprovalModal(true);
  };

  const handleDelete = async () => {
    const confirmed = await alerts.confirm(
      'Are you sure you want to delete vacation request?',
    );
    if (!confirmed) {
      return;
    }
    try {
      await api.delete(
        `/v1/payments/freelancers/holidays/requests/${request.id}`,
      );
      await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
        exact: false,
      });
      await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
        exact: false,
      });
      alerts.success('Vacation request deleted successfully');
    } catch (e) {
      alerts.error('Failed to delete vacation request');
    }
  };

  return (
    <>
      <Tr
        $isClickable={activeStatus.value === VacationStatus.Pending}
        onClick={onRowClick}
      >
        <Td>{request.freelancerName}</Td>
        {activeStatus.value === VacationStatus.Pending ? (
          <>
            <Td>
              {DateTime.fromISO(request.requestedAt).toFormat('yyyy/LL/dd') ||
                '-'}
            </Td>
            <VacationPeriodTd>{vacationPeriod}</VacationPeriodTd>
            <Td>{deductedDaysText}</Td>
          </>
        ) : (
          <>
            <VacationPeriodTd>{vacationPeriod}</VacationPeriodTd>
            <Td>{deductedDaysText}</Td>
          </>
        )}
        <Td>
          <VacationTypeBadge type={request.type} />
        </Td>
        <Td>{availableDaysText}</Td>
        <Td>
          <VacationStatusDot status={request.teamLeadStatus} />
        </Td>
        <Td>
          <VacationStatusDot status={request.hrStatus} />
        </Td>
        {canManageRequests && (
          <Td $actionsCol>
            <ActionsWrap>
              <ActionsDropdown>
                <ActionsDropdownButton
                  as="button"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setShowEditModal(true);
                  }}
                >
                  <PencilIcon />
                  Edit
                </ActionsDropdownButton>
                <ActionsDropdownButton
                  as="button"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                >
                  <TrashIcon />
                  Delete
                </ActionsDropdownButton>
              </ActionsDropdown>
            </ActionsWrap>
          </Td>
        )}
      </Tr>
      <AnimatePresence>
        {showApprovalModal && (
          <VacationApprovalModal
            request={request}
            onClose={() => setShowApprovalModal(false)}
          />
        )}
        {showEditModal && (
          <FreelancerVacationRequestModal
            request={request}
            onClose={() => setShowEditModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VacationApprovalTableRow;
